import { useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { AUDIT_REASON_MIN_LENGTH, RAIL_MAXIMUM_FLOOR_CENTAVOS } from '@dayon/contracts';
import { formatPesos, parsePesos } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';

/**
 * Payments → a rail's Limits: the least and the most one deposit or withdrawal over it may be.
 *
 * THE WRITE:
 *   POST /api/payments/methods/:id/limits   { minAmount, maxAmount, reason } in centavos,
 *                                           behind platform.manage
 *
 * PESOS ON THE SCREEN, CENTAVOS ON THE WIRE. What is typed is parsed with `parsePesos` — never a
 * float — and sent as a whole number of centavos in a string, as every amount crosses the wire.
 *
 * THE MAXIMUM IS THE ONE STARPAY'S REFUSALS SEND AN OPERATOR TO. StarPay refuses an amount over its
 * own per-transfer limit with AM02 or AM13, and the Payout panel's Cause for those codes names this
 * control (`advice-names-a-real-control.test.ts` holds the two together). Until this, the maximum
 * could be changed only by SQL.
 *
 * Refused beside the field, before the request, where the contract would refuse it: a maximum under
 * ₱100.00, and a maximum that is not above the minimum. A range a brand's own limits would fall
 * outside of is refused by the route, naming the brand. A request is held to the limits when it is
 * made, so requests already made are not touched — said before the press.
 */

/** The rail as the Payments screen read it: what this dialog needs of the row. */
export interface RailForLimits {
  id: string;
  name: string;
  provider: string;
  min_amount: string;
  max_amount: string;
  supports_deposit: boolean;
  supports_withdrawal: boolean;
}

/** What one movement over the rail is, by the directions it carries — the fields are labelled by it. */
const perOf = (r: Pick<RailForLimits, 'supports_deposit' | 'supports_withdrawal'>): string =>
  r.supports_deposit && r.supports_withdrawal
    ? 'per deposit or withdrawal'
    : r.supports_deposit
      ? 'per deposit'
      : 'per withdrawal';

/** "50,000.00" — the figure as it is typed, which parsePesos reads back. */
const asTyped = (centavos: string): string => formatPesos(BigInt(centavos)).replace('₱', '');

/** The three boxes this dialog draws, by the names the route refuses them under. */
export const LIMIT_FIELDS: readonly string[] = ['minAmount', 'maxAmount', 'reason'];

/**
 * WHERE A REFUSAL'S FIELD PROBLEMS GO. One naming a box this dialog draws is shown beside that box;
 * one naming anything else has no box to sit beside, and kept as field state it would show nowhere —
 * the dialog would stay open saying nothing. Those go to the error dialog, in the route's words.
 */
export function splitLimitProblems(fields: Readonly<Record<string, string>>): {
  inline: Record<string, string>;
  elsewhere: string[];
} {
  const inline: Record<string, string> = {};
  const elsewhere: string[] = [];
  for (const [key, problem] of Object.entries(fields)) {
    if (LIMIT_FIELDS.includes(key)) inline[key] = problem;
    else elsewhere.push(problem);
  }
  return { inline, elsewhere };
}

/** A typed amount in centavos, or why it is not one. */
function centavosOf(typed: string): { centavos: bigint } | { problem: string } {
  try {
    const centavos = parsePesos(typed);
    return centavos > 0n ? { centavos } : { problem: 'Enter an amount above zero.' };
  } catch {
    return { problem: 'Enter a peso amount with at most two decimals, e.g. 30,000.00.' };
  }
}

export function RailLimitsDialog({
  rail,
  onClose,
  onChanged,
}: {
  /** The rail whose limits are being set, or null when the dialog is closed. */
  rail: RailForLimits | null;
  onClose: () => void;
  /** After the limits were changed, so the rail list is read again. */
  onChanged: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [reason, setReason] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  /* Every opening starts from the rail's own limits and an empty reason: a figure or a reason typed
     for one rail must never be sent against another. Adjusted during render, not in an effect. */
  const [openedFor, setOpenedFor] = useState<string | null>(null);
  if (openedFor !== (rail?.id ?? null)) {
    setOpenedFor(rail?.id ?? null);
    setMin(rail ? asTyped(rail.min_amount) : '');
    setMax(rail ? asTyped(rail.max_amount) : '');
    setReason('');
    setFields({});
  }

  const apply = async () => {
    if (rail === null || busy) return;
    const problems: Record<string, string> = {};
    const low = centavosOf(min);
    const high = centavosOf(max);
    if ('problem' in low) problems['minAmount'] = low.problem;
    if ('problem' in high) problems['maxAmount'] = high.problem;
    else if (high.centavos < RAIL_MAXIMUM_FLOOR_CENTAVOS) {
      problems['maxAmount'] = `Set the maximum at ${formatPesos(RAIL_MAXIMUM_FLOOR_CENTAVOS)} or above.`;
    } else if ('centavos' in low && high.centavos <= low.centavos) {
      problems['maxAmount'] = 'The maximum must be above the minimum.';
    }
    if (
      !problems['minAmount'] &&
      !problems['maxAmount'] &&
      'centavos' in low &&
      'centavos' in high &&
      low.centavos === BigInt(rail.min_amount) &&
      high.centavos === BigInt(rail.max_amount)
    ) {
      problems['maxAmount'] = `${rail.name} already allows ${formatPesos(low.centavos)} to ${formatPesos(high.centavos)}. Change a figure, or cancel.`;
    }
    if (reason.trim().length < AUDIT_REASON_MIN_LENGTH) {
      problems['reason'] = `Say why the limits are changing — at least ${AUDIT_REASON_MIN_LENGTH} characters.`;
    }
    setFields(problems);
    if (Object.keys(problems).length > 0 || !('centavos' in low) || !('centavos' in high)) return;

    setBusy(true);
    try {
      const result = await api.post<{ effect: string }>(
        `/api/payments/methods/${encodeURIComponent(rail.id)}/limits`,
        {
          minAmount: low.centavos.toString(),
          maxAmount: high.centavos.toString(),
          reason: reason.trim(),
        },
      );
      toast(result.effect);
      onChanged();
      onClose();
    } catch (error: unknown) {
      /* Field-level problems stay beside their field — the three boxes this dialog draws; a problem
         naming anything else, and every other failure, opens the error dialog. */
      if (error instanceof ApiRequestError && error.fields) {
        const { inline, elsewhere } = splitLimitProblems(error.fields);
        setFields(inline);
        if (elsewhere.length === 0) return;
        showError({
          title: `The limits on ${rail.name} were not changed`,
          message: elsewhere.join(' '),
          code: error.code,
        });
        return;
      }
      showError({
        title: `The limits on ${rail.name} were not changed`,
        message: error instanceof ApiRequestError ? error.message : 'Nothing was changed on this rail.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  const per = rail ? perOf(rail) : '';

  return (
    <Dialog
      open={rail !== null}
      onClose={onClose}
      title={rail ? `Limits for ${rail.name}` : ''}
      dismissible={false}
      actions={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={busy} onClick={() => void apply()}>
            Set the limits
          </Button>
        </>
      }
    >
      {rail === null ? null : (
        <div className="stack">
          <p className="text-muted" style={{ margin: 0 }}>
            Today {rail.name} allows {formatPesos(BigInt(rail.min_amount))} to{' '}
            {formatPesos(BigInt(rail.max_amount))} {per}.
          </p>

          {rail.provider === 'STARPAY' ? (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                StarPay refuses an amount above its own limit with code AM02/AM13; set the maximum at
                or below what UAT showed.
              </div>
            </div>
          ) : null}

          <div className="halves">
            <Field label={`Minimum ${per} (PHP)`} error={fields['minAmount']}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  inputMode="decimal"
                  value={min}
                  onChange={(e) => setMin(e.target.value)}
                />
              )}
            </Field>
            <Field
              label={`Maximum ${per} (PHP)`}
              error={fields['maxAmount']}
              hint={`${formatPesos(RAIL_MAXIMUM_FLOOR_CENTAVOS)} or above, and above the minimum.`}
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  inputMode="decimal"
                  value={max}
                  onChange={(e) => setMax(e.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="notice notice-warn">
            <Icon name="warning" />
            <div>
              A request is held to these limits when it is made: from now on one outside them is
              refused, and requests already made are not touched. A brand that narrows this
              rail&rsquo;s range for itself keeps its own limits, and a range they would fall outside
              of is refused, naming the brand.
            </div>
          </div>

          <Field
            label="Reason"
            error={fields['reason']}
            hint={`At least ${AUDIT_REASON_MIN_LENGTH} characters, kept on the audit trail with your name — the figure's source, for example what StarPay confirmed in writing.`}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                minLength={AUDIT_REASON_MIN_LENGTH}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="StarPay confirmed a per-transfer limit of ₱30,000 in writing on 12 Oct."
              />
            )}
          </Field>
        </div>
      )}
    </Dialog>
  );
}
